// Ported from horizontal-actions-dashboard/frontend/src/features/regions/RegionDetailPage.tsx,
// MUI-styled. Reached only via direct navigation/links (not part of the Terminals tab menu).

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { getRegionDetail, ApiError } from '../api/horizontalActionsApi';
import type { RegionDetailResponse } from '../api/horizontalActionsTypes';
import { TerminalsLoadingState, TerminalsErrorState } from './terminals-shared/TerminalsStateViews';
import { TerminalsProgressBar } from './terminals-shared/TerminalsProgressBar';
import { TerminalsRiskScoreCell } from './terminals-shared/TerminalsRiskScore';
import { TerminalsPriorityBadge } from './terminals-shared/TerminalsPriorityBadge';
import { TerminalsStatusBadge } from './terminals-shared/TerminalsStatusBadge';

export default function TerminalsRegionDetail() {
  const { code } = useParams<{ code: string }>();
  const [data, setData] = useState<RegionDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadData = useCallback(async () => {
    if (!code) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getRegionDetail(code);
      setData(result);
    } catch (err) {
      setError(err instanceof ApiError ? err : new Error('Failed to load region.'));
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) return <Box sx={{ p: 3 }}><TerminalsLoadingState label="Loading region…" /></Box>;
  if (error) return <Box sx={{ p: 3 }}><TerminalsErrorState error={error} onRetry={loadData} /></Box>;
  if (!data) return null;

  const { summary, terminals, open_actions } = data;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Link to="/terminals-summary">&larr; Back to dashboard</Link>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {summary.region_display_name}
        </Typography>
        <TerminalsProgressBar pct={summary.kpi_completion_pct} label={`${summary.region_display_name} weighted completion`} />
        <Typography variant="body2" sx={{ mt: 1.5, color: '#5a6b7c' }}>
          {summary.kpi_completed} completed of {summary.kpi_total} actions to complete across{' '}
          {summary.terminal_count} terminals. {summary.kpi_in_progress} open, {summary.kpi_open_high} open High-priority,{' '}
          {summary.kpi_overdue} overdue.
        </Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Terminal comparison
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Terminal</TableCell>
                <TableCell>Completion</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell>Total actions</TableCell>
                <TableCell>Open</TableCell>
                <TableCell>Overdue Actions</TableCell>
                <TableCell>Open High</TableCell>
                <TableCell>Risk Score</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {terminals.map((t) => (
                <TableRow key={t.terminal_name} hover>
                  <TableCell component="th" scope="row">
                    <Link to={`/terminals/${encodeURIComponent(t.terminal_name)}`}>{t.terminal_name}</Link>
                  </TableCell>
                  <TableCell sx={{ minWidth: 160 }}>
                    <TerminalsProgressBar pct={t.kpi_completion_pct} label={`${t.terminal_name} completion`} />
                  </TableCell>
                  <TableCell>{t.kpi_completed}</TableCell>
                  <TableCell>{t.kpi_total}</TableCell>
                  <TableCell>{t.kpi_in_progress}</TableCell>
                  <TableCell>{t.kpi_overdue > 0 ? <strong>{t.kpi_overdue}</strong> : 0}</TableCell>
                  <TableCell>{t.kpi_open_high > 0 ? <strong>{t.kpi_open_high}</strong> : 0}</TableCell>
                  <TerminalsRiskScoreCell score={t.risk_score} band={t.risk_band} />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Open actions by priority
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Priority</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Terminal</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Reference</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {open_actions.map((a) => (
                <TableRow key={`${a.action_id}-${a.terminal}`} hover>
                  <TableCell>
                    <TerminalsPriorityBadge priority={a.priority} />
                  </TableCell>
                  <TableCell>{a.action_title}</TableCell>
                  <TableCell>
                    <Link to={`/terminals/${encodeURIComponent(a.terminal)}`}>{a.terminal}</Link>
                  </TableCell>
                  <TableCell>
                    <TerminalsStatusBadge status={a.normalized_status} />
                  </TableCell>
                  <TableCell>
                    {a.task_url ? (
                      <a href={a.task_url} target="_blank" rel="noreferrer noopener">
                        {a.task_reference ?? 'Open'}
                      </a>
                    ) : (
                      a.task_reference ?? '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {open_actions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>No open actions in this region.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
